import localforage from "localforage";

type TokenCache = {
  value: string | null;
  fetchedAt: number;
};

const TOKEN_TTL_MS = 30_000;
const cache: TokenCache = { value: null, fetchedAt: 0 };

const getTokenFromWindowEnv = (): string | null => {
  const w = window as any;
  const token = w && w.Penmate && w.Penmate.env && w.Penmate.env.authToken;
  return typeof token === "string" && token.length ? token : null;
};

const storage = localforage.createInstance({
  name: "__PMdb",
  storeName: "appStore",
});

const ensureDriver = (() => {
  let promise: Promise<void> | null = null;
  return () => {
    if (promise) return promise;
    // Match Ionic Storage config (prefers websql/localstorage on web).
    promise = storage
      .setDriver([localforage.WEBSQL, localforage.LOCALSTORAGE])
      .then(() => undefined)
      .catch(() => undefined);
    return promise;
  };
})();

const readFromLocalStorageFallback = (): string | null => {
  try {
    const direct = window.localStorage.getItem("id_token");
    if (direct && direct.length) return direct;
  } catch {
    // ignore
  }

  try {
    // Ionic Storage + localforage localStorage driver uses a key prefix like:
    // `${name}/${storeName}/` (e.g. "__PMdb/appStore/")
    for (let i = 0; i < window.localStorage.length; i++) {
      const key = window.localStorage.key(i) || "";
      if (!/id_token/i.test(key)) continue;
      const value = window.localStorage.getItem(key);
      if (value && value.length) return value;
    }
  } catch {
    // ignore
  }

  return null;
};

export async function getAuthToken(): Promise<string | null> {
  const fromEnv = getTokenFromWindowEnv();
  if (fromEnv) return fromEnv;

  const now = Date.now();
  if (cache.fetchedAt && now - cache.fetchedAt < TOKEN_TTL_MS) return cache.value;

  try {
    await ensureDriver();
    const token = await storage.getItem<string>("id_token");
    const value =
      typeof token === "string" && token.length ? token : readFromLocalStorageFallback();
    cache.value = value && value.length ? value : null;
    cache.fetchedAt = now;
    return cache.value;
  } catch {
    cache.value = readFromLocalStorageFallback();
    cache.fetchedAt = now;
    return null;
  }
}
